import { BlankSlate } from "@msuite/ui/blank-slate";
import { Button } from "@msuite/ui/button";
import { ContentBox } from "@msuite/ui/content-box";
import { useDisclosure } from "@msuite/ui/hooks";
import { Icon, icon } from "@msuite/ui/icons";
import { LoadingScreen } from "@msuite/ui/loading-screen";
import { useTranslation } from "@msuite/ui/localization";
import { invalidate, useQueryClient } from "@msuite/ui/query";
import { Tabs } from "@msuite/ui/tabs";
import { ResponseError } from "@msuite/utils/errors";
import { Fragment } from "react";
import { useParams } from "react-router";
import { Can } from "@/components/citadel/can";
import { ReturnOrderSheets } from "@/components/stock/return-order-sheets";
import { useReturnOrder } from "@/hooks/use-return-order";
import { EditReturnOrderVehiclesModal } from "@/modals/stock/edit-return-order-vehicles-modal";
import { ReturnOrderCarriers } from "./panels/return-order-carriers";
import { ReturnOrderGeneral } from "./panels/return-order-general";
import { ReturnOrderReconciliation } from "./panels/return-order-reconciliation";
import { ReturnOrderHeader } from "./return-order-header";

/**
 * One return run: the site being cleared, the trucks fetching it, and the account of what has come
 * back off it.
 *
 * `read Stock` covers the whole page, so a refusal is the page rather than one card of it — the
 * same shape the order detail takes.
 *
 * The fleet is the run's only editable part, so it is what the header's action opens; everything
 * else on the page is an account of work already done. The same modal hangs off the vehicles card
 * in the general panel, because that is where a reader who is already looking at the trucks reaches
 * for it.
 */
export function ReturnOrder() {
	/** Hooks */
	const { t } = useTranslation();
	const qc = useQueryClient();
	const returnOrderId = useParams().returnOrderId as string;
	const editVehiclesModal = useDisclosure();

	/** Query */
	const { returnOrder, isLoading, isError, error } = useReturnOrder(returnOrderId);

	/** Functions */
	async function handleOnEditComplete() {
		// `return-order-files` among them: changing the fleet reprints the sheet, and the sheets tab
		// would otherwise keep offering the file the new one replaced.
		await qc.invalidateQueries({
			predicate: invalidate(["return-order", "return-orders", "return-order-files"]),
		});
	}

	/** Computed */
	const isForbidden = error instanceof ResponseError && error.status === 403;

	/** Guard */
	if (isForbidden) {
		return (
			<ContentBox>
				<BlankSlate
					Icon={icon("Lock")}
					label={t("general.no_access")}
					withAccessory={false}
				/>
			</ContentBox>
		);
	}
	if (isError) {
		return (
			<ContentBox>
				<BlankSlate label={t("stock.return_order_not_loaded")} />
			</ContentBox>
		);
	}
	if (isLoading) return <LoadingScreen />;
	if (!returnOrder) {
		return (
			<ContentBox>
				<BlankSlate label={t("stock.return_order_not_loaded")} />
			</ContentBox>
		);
	}

	/** Render */
	return (
		<Fragment>
			<ContentBox bottomPadding="none">
				<ReturnOrderHeader returnOrderId={returnOrderId} />
				<Tabs
					header={() => (
						<Can
							action="update"
							subject="Stock"
						>
							<Button
								icon={<Icon name="Edit" />}
								onClick={editVehiclesModal.onOpen}
								variant="primary"
							>
								{t("stock.return_edit_vehicles")}
							</Button>
						</Can>
					)}
					groups={[
						{
							name: "general",
							panels: [
								{
									label: t("general.general"),
									cyId: "return-order-general-tab",
									panel: () => <ReturnOrderGeneral returnOrderId={returnOrderId} />,
									errors: [],
								},
							],
						},
						{
							name: "information",
							panels: [
								{
									label: t("stock.return_reconciliation"),
									cyId: "return-order-reconciliation-tab",
									panel: () => <ReturnOrderReconciliation returnOrderId={returnOrderId} />,
									errors: [],
								},
								{
									label: t("stock.load_carriers"),
									cyId: "return-order-carriers-tab",
									panel: () => <ReturnOrderCarriers returnOrderId={returnOrderId} />,
									errors: [],
								},
								{
									label: t("stock.return_sheets"),
									cyId: "return-order-sheets-tab",
									panel: () => <ReturnOrderSheets returnOrderId={returnOrderId} />,
									errors: [],
								},
							],
						},
					]}
				/>
			</ContentBox>
			{editVehiclesModal.isOpen && (
				<EditReturnOrderVehiclesModal
					isOpen={editVehiclesModal.isOpen}
					onClose={editVehiclesModal.onClose}
					onComplete={handleOnEditComplete}
					returnOrderId={returnOrderId}
				/>
			)}
		</Fragment>
	);
}
